// react
import styled from 'styled-components';

// components
import { Container, Icon, Card } from '@/components/atoms';
import { BackButton, FloatingActions } from '@/components/molecules';
import { Icons } from '@/components/atoms/Icon';

// fixture
import {
  financialInfoPageItems,
  financialInfoCardDetails,
} from '@/components/pages/FinancialStatusStart/fixtures';

/**
 * Displays the Financial Status Start Page before user starts the Financial Status form.
 */
export const FinancialStatusStart = ({
  onClick,
}: FinancialStatusStartProps) => {
  return (
    <PageWrapper>
      <BackButton />

      <Container size='xl'>
        <ContentWrapper>
          <PageTitle>My Funding Consultation</PageTitle>

          <ContentCard color='blue' isBordered={false} elevation={0}>
            <table>
              {financialInfoPageItems.map(
                ({ icon, text, iconColor }, index) => (
                  <tr key={index}>
                    <IconCell>
                      <ColoredIcon
                        icon={icon as Icons}
                        iconColor={iconColor}
                        fontSize={30}
                      />
                    </IconCell>
                    <TextCell>{text}</TextCell>
                  </tr>
                ),
              )}
            </table>
          </ContentCard>

          <InfoCard
            color={financialInfoCardDetails.iconColor}
            isBordered={false}
            elevation={0}
          >
            <InfoCardDetails>
              <Icon
                icon={financialInfoCardDetails.icon as Icons}
                fontSize={18}
              />
              <TextWrapper>
                <InfoCardTitle>{financialInfoCardDetails.title}</InfoCardTitle>
                <InfoCardContent>
                  {financialInfoCardDetails.content}
                </InfoCardContent>
              </TextWrapper>
            </InfoCardDetails>
          </InfoCard>

          <FloatingActions
            actions={[
              {
                label: 'Start Funding Consultation',
                onPress: onClick,
              },
            ]}
          />
        </ContentWrapper>
      </Container>
    </PageWrapper>
  );
};

const PageWrapper = styled.div`
  padding: ${(props) => props.theme.spacing[24]};
  padding-bottom: ${(props) => props.theme.spacing[96]};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.theme.spacing[24]};
`;

const PageTitle = styled.div`
  color: ${(props) => props.theme.color.neutral[90]};
  font: ${(props) => props.theme.typography.heading['3xl']};
`;

const IconCell = styled.td`
  padding-right: ${(props) => props.theme.spacing[12]};
`;

const ContentCard = styled(Card)`
  padding: ${(props) => props.theme.spacing[32]};
`;

const TextCell = styled.td`
  font: ${(props) => props.theme.typography.body.md.regular};
  padding: ${(props) => props.theme.spacing[12]} 0;
`;

const ColoredIcon = styled(Icon)<{ iconColor: 'blue' | 'success' | 'primary' }>`
  color: ${(props) =>
    props.iconColor === 'blue'
      ? props.theme.color.actionPlanCards.blue.primary
      : props.iconColor === 'success'
        ? props.theme.color.success[40]
        : props.theme.color.primary[70]};
`;

const InfoCard = styled(Card)`
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing[8]};
  padding: ${(props) => props.theme.spacing[16]};
  font: ${(props) => props.theme.typography.body.md.regular};
`;

const InfoCardDetails = styled.div`
  display: flex;
  gap: ${(props) => props.theme.spacing[8]};
  padding-left: ${(props) => props.theme.spacing[8]};
`;

const InfoCardTitle = styled.div`
  padding-bottom: ${(props) => props.theme.spacing[8]};
  color: ${(props) => props.theme.color.primary[80]};
  font: ${(props) => props.theme.typography.heading['1xl']};
`;

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoCardContent = styled.div`
  color: ${(props) => props.theme.color.neutral[90]};
  font: ${(props) => props.theme.typography.body.md.regular};
`;

interface FinancialStatusStartProps {
  onClick: () => void;
}

FinancialStatusStart.displayName = 'FinancialStatusStart';

export default FinancialStatusStart;
